import type { ConfigServerOverview } from "@/api/config-server-stages";
import { DOMAIN_NODES, domainNodeStates } from "@/components/agents/configuration-server/stages/extractionProgress";
import { FAILURE_STATUSES, TABLE_STATUS_LABEL } from "@/components/agents/configuration-server/stages/extractionStatus";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { errorDetailOr } from "@/lib/api-error";
import { configurationErrorMessage } from "@/lib/config-server-messages";
import { getScopeApproval } from "@/api/scope-activation";
import { AgentInputForm } from "@/components/agents/AgentInputForm";
import { ConfigServerAlert } from "@/components/agents/configuration-server/ConfigServerAlert";
import { ScopePrerequisiteBanner } from "@/components/agents/configuration-server/ScopePrerequisiteBanner";
import {
  DecisionBar,
  ExtractionDashboard,
} from "@/components/agents/configuration-server/stages/ExtractionDashboard";
import { OrgStructureSyncAction } from "@/components/agents/configuration-server/stages/OrgStructureSyncAction";
import { StageHeader } from "@/components/agents/configuration-server/stages/stageNav";
import { ExtractionDomains } from "@/components/agents/configuration-server/stages/ExtractionDomains";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAgentExecution } from "@/hooks/useAgentExecution";
import { errMessage } from "@/lib/reference-data-edit";
import { STREAM_INTERRUPTED_CODE, STREAM_TRANSPORT_ERROR_CODE } from "@/lib/stream-error-codes";
import { useAgentSchema, usePreparedInputs, usePipelineStatus, useExecution } from "@/hooks/useApi";
import { extractionRefreshFlags, matchesReadyExtraction, stageKeys, useConfigServerOverview, useExtractionRefresh, useExtractionSummary, useStartExtractionRefresh } from "@/hooks/useConfigServerStages";
import { useConfigTables, useOrgStructureSyncStatus, useSyncOrgStructure } from "@/hooks/useConfigurationServer";
import { ExtractionProgressBar } from "./ExtractionProgressBar";

const AGENT_ID = "configuration_server";

// Padrão de abas do app: sublinhado amarelo na ativa e contador em pílula.
const FAILURE_TAB_CLASS =
  "h-auto gap-2 rounded-none border-0 border-b-[3px] border-b-transparent px-5 py-3.5 font-brand text-sm font-semibold text-[var(--exed-ink-500)] shadow-none transition-colors hover:text-[var(--exed-ink-900)] data-[state=active]:border-b-[var(--exed-gold)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--exed-ink-900)] data-[state=active]:shadow-none";
const FAILURE_TAB_COUNT_CLASS =
  "rounded-full bg-[#eef1f6] px-2 py-0.5 font-mono text-[11px] font-medium text-[var(--exed-ink-500)] group-data-[state=active]:bg-[rgba(255,200,61,0.18)] group-data-[state=active]:text-[#8a6100]";
const FAILURE_PAGE_SIZE = 100;
type FailureStatus = (typeof FAILURE_STATUSES)[number];

export default function ExtractionStage() {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const syncOrgStructure = useSyncOrgStructure(projectId);
  const orgSyncStatus = useOrgStructureSyncStatus(projectId);
  const startRefresh = useStartExtractionRefresh(projectId);
  const syncOrgStructureRef = useRef(syncOrgStructure.mutateAsync);
  syncOrgStructureRef.current = syncOrgStructure.mutateAsync;
  const execution = useAgentExecution();
  const [executionProjectId, setExecutionProjectId] = useState(projectId);
  const ownsExecution = executionProjectId === projectId;
  const localExecutionStatus = ownsExecution ? execution.status : "idle";
  const { data: schema } = useAgentSchema(projectId, AGENT_ID);
  const { data: preparedInputs } = usePreparedInputs(projectId, AGENT_ID);
  const { data: overview, isError: overviewError, refetch: refetchOverview } = useConfigServerOverview(projectId, localExecutionStatus === "running");
  // A consulta do refresh só liga quando o overview diz que há atualização rodando (spec 2026-10-29, D14).
  const extractionRefresh = useExtractionRefresh(projectId, { watch: overview?.extraction_refresh?.running === true });
  const { data: pipeline, isError: pipelineError, refetch: refetchPipeline } = usePipelineStatus(projectId);
  const latestExecution = pipeline?.agents.find((agent) => agent.agent_id === AGENT_ID)?.latest_execution;
  const localTerminal = localExecutionStatus === "failed" || localExecutionStatus === "completed";
  const [supersededExecutionId, setSupersededExecutionId] = useState<string | null>(null);
  const observedLocalExecution = useRef<{ projectId: string | undefined; executionId: string; startedAt: number; completedAt: number } | null>(null);
  useEffect(() => {
    if (ownsExecution && execution.executionId && latestExecution?.execution_id === execution.executionId) {
      observedLocalExecution.current = {
        projectId, executionId: execution.executionId,
        startedAt: Date.parse(latestExecution.started_at ?? ""),
        completedAt: Date.parse(latestExecution.completed_at ?? ""),
      };
    }
  }, [projectId, ownsExecution, execution.executionId, latestExecution]);
  const observed = observedLocalExecution.current;
  const observedLocal = observed && observed.projectId === projectId
    && observed.executionId === execution.executionId ? observed : null;
  const needsLocalProvenance = localTerminal && !!execution.executionId && !!latestExecution
    && latestExecution.execution_id !== execution.executionId && !observedLocal;
  const localDetail = useExecution(projectId, AGENT_ID, needsLocalProvenance ? execution.executionId : undefined);
  const authoritativeLocal = localDetail.data?.id === execution.executionId
    && localDetail.data.project_id === projectId ? localDetail.data : null;
  const localStartedAt = observedLocal?.startedAt ?? Date.parse(authoritativeLocal?.started_at ?? "");
  const localCompletedAt = observedLocal?.completedAt ?? Date.parse(authoritativeLocal?.completed_at ?? "");
  const remoteStartedAt = Date.parse(latestExecution?.started_at ?? "");
  const remoteCompletedAt = Date.parse(latestExecution?.completed_at ?? "");
  const newerRemote = latestExecution && latestExecution.execution_id !== execution.executionId
    && (Number.isFinite(remoteStartedAt) && Number.isFinite(localStartedAt)
      ? remoteStartedAt > localStartedAt
      : remoteCompletedAt > localCompletedAt);
  // Falha local com a mesma execução ainda ativa no servidor. Quando o navegador
  // ou a API desconectam, o agents cancela a execução e grava `failed`
  // (`_bounded_stream` captura `GeneratorExit`/`CancelledError`); esta regra
  // serve ao caso em que o agents não conseguiu gravar o fim (travado ou morto
  // sem reboot) e dura até o servidor confirmar o status, quando o servidor
  // passa a ser a fonte, como acontece quando outra execução assume. A regra
  // vale só para queda de conexão: um evento `error` do servidor (sem
  // `errorCode`) sempre mostra a falha, mesmo que o resumo em cache ainda
  // aponte a execução como ativa.
  const connectionDropped = execution.errorCode === STREAM_INTERRUPTED_CODE
    || execution.errorCode === STREAM_TRANSPORT_ERROR_CODE;
  const aliveOnServer = localExecutionStatus === "failed" && connectionDropped && execution.executionId != null
    && overview?.active_run?.kind === "extraction" && overview.active_run.id === execution.executionId;
  const supersededByNewerRunNow = localTerminal && execution.executionId != null
    && ((overview?.active_run?.kind === "extraction" && overview.active_run.id !== execution.executionId) || newerRemote);

  // A execução mais nova continua vencendo mesmo quando deixa de estar ativa.
  // A decisão gravada só vale até o servidor confirmar a falha desta mesma
  // execução: aí o erro local volta a ser mostrado.
  const serverFailedSameRun = latestExecution?.execution_id === execution.executionId
    && latestExecution?.status === "failed";
  const localSuperseded = supersededByNewerRunNow || aliveOnServer
    || (execution.executionId != null && execution.executionId === supersededExecutionId
      && !serverFailedSameRun);
  const serverCompletedSameRun = !localSuperseded
    && latestExecution?.execution_id === execution.executionId
    && latestExecution.status === "completed";
  const progressStatus = serverCompletedSameRun ? "completed"
    : localSuperseded ? "idle" : localExecutionStatus;
  // Grava a decisão de seguir o servidor: quando a extração terminar, o
  // `aliveOnServer` apaga, e sem isso a tela voltaria a "não terminou" enquanto
  // o status em cache da execução ainda está antigo.
  useEffect(() => {
    if (supersededByNewerRunNow || aliveOnServer) setSupersededExecutionId(execution.executionId);
  }, [supersededByNewerRunNow, aliveOnServer, execution.executionId]);
  const running = progressStatus === "running"
    || (progressStatus !== "failed" && progressStatus !== "completed" && overview?.active_run?.kind === "extraction");
  const expectedReady = progressStatus === "completed" ? {
    executionId: execution.executionId,
    artifactId: typeof execution.outputs?.artifact_id === "string" ? execution.outputs.artifact_id : execution.liveArtifactId,
  } : undefined;
  const { data: summary, isLoading: summaryLoading, isError: summaryError, refetch: refetchSummary } = useExtractionSummary(projectId, running, expectedReady);
  const currentExecutionId = localExecutionStatus === "running" || localExecutionStatus === "paused"
    ? execution.executionId
    : overview?.active_run?.kind === "extraction" ? overview.active_run.id : null;
  const currentSummary = expectedReady ? matchesReadyExtraction(summary, expectedReady)
    : !running || (!!summary?.artifact_id && summary.artifact_status === "draft"
      && currentExecutionId != null && summary.execution_id === currentExecutionId);
  const summaryPending = summaryLoading || !summary || !currentSummary;
  const [uiProjectId, setUiProjectId] = useState(projectId);
  const ownsUi = uiProjectId === projectId;
  const [showInputSheet, setShowInputSheet] = useState(false);
  const [selectedFailureStatus, setFailureStatus] = useState<FailureStatus | null>(null);
  const [failureArtifactId, setFailureArtifactId] = useState<string | null>(null);
  const failureStatus = ownsUi && currentSummary && failureArtifactId === summary?.artifact_id
    ? selectedFailureStatus : null;
  const [failurePage, setFailurePage] = useState(1);
  useEffect(() => {
    if (uiProjectId === projectId) return;
    setUiProjectId(projectId);
    setShowInputSheet(false);
    setFailureStatus(null);
    setFailureArtifactId(null);
    setFailurePage(1);
    setSupersededExecutionId(null);
  }, [projectId, uiProjectId]);
  const { data: scopeApproval, isError: scopeError, refetch: refetchScope } = useQuery({
    queryKey: ["scope-approval", projectId],
    queryFn: () => getScopeApproval(projectId!),
    enabled: !!projectId,
  });
  const scopeApproved = scopeApproval?.approved ?? false;
  // Completed significa executado; failed sozinho ainda permite reextração (§4.4).
  const executedSomething = overview?.has_completed_items ?? false;
  const busy = !!overview?.active_run || localExecutionStatus === "running" || localExecutionStatus === "paused";
  // O Diagnóstico mudou depois da extração: só a estrutura organizacional precisa vir de novo.
  const diagnosticChanged = orgSyncStatus.data?.needs_sync === true;
  // Upload novo depois da extração: a atualização traz só o que mudou (a mesma da etapa 2).
  const refreshFlags = extractionRefreshFlags(overview, extractionRefresh.data);
  const refreshing = refreshFlags.running;
  const updateAvailable = refreshFlags.available || refreshing;
  const refreshProgress = extractionRefresh.data?.progress ?? null;
  // Fim da atualização observada (running -> terminado): avisa o resultado em toast, como o Gerenciamento.
  // Um refresh que já chega terminado na montagem não avisa nada.
  const previousRefreshStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const result = extractionRefresh.data?.refresh;
    const status = result?.status;
    if (result && previousRefreshStatus.current === "running" && status && status !== "running") {
      if (status === "done") {
        const records = result.records_read;
        toast.success(
          result.message
            ? t("Nothing new to extract")
            : typeof records === "number"
              ? t("{{tables}} table(s), {{records}} row(s) re-read, {{fields}} new field(s)", {
                  tables: result.tables_reread ?? 0,
                  records: records.toLocaleString(i18n.language),
                  fields: result.fields_added ?? 0,
                })
              : t("{{tables}} table(s) re-read, {{fields}} new field(s)", { tables: result.tables_reread ?? 0, fields: result.fields_added ?? 0 }),
        );
      } else {
        toast.error(result.errors?.[0] ?? t("The update could not be extracted."));
      }
    }
    previousRefreshStatus.current = status;
  }, [extractionRefresh.data, t, i18n.language]);
  const activeProgress = overview?.active_run?.kind === "extraction" ? overview.active_run.progress ?? null : null;
  // A extração completa roda uma vez: concluída a primeira (a etapa fica "done"; uma que falhou volta a
  // "not_started" e deixa tentar de novo), ou executada a primeira configuração, o botão trava e só
  // as atualizações (upload novo e Diagnóstico) seguem.
  const extractionDone = overview?.stages.extraction.state === "done";
  const fullExtractionLocked = executedSomething || extractionDone;
  const hasPendingUpdate = diagnosticChanged || updateAvailable;
  const canRun = !!projectId && !!overview && !overviewError && !scopeError && scopeApproved && !fullExtractionLocked && !busy;

  const failingStatuses = useMemo(
    () => FAILURE_STATUSES.filter((status) => (summary?.tables_by_status[status] ?? 0) > 0),
    [summary],
  );
  const { data: failedTables, isLoading: failedLoading, isPlaceholderData, isError: failedError, refetch: refetchFailures } = useConfigTables(
    failureStatus && failureArtifactId ? projectId : undefined,
    failureStatus && failureArtifactId ? { artifact_id: failureArtifactId, status: failureStatus, page: failurePage, limit: FAILURE_PAGE_SIZE } : {},
  );
  const failedPending = failedLoading || isPlaceholderData;
  const failurePages = Math.max(1, Math.ceil((failedTables?.total ?? 0) / FAILURE_PAGE_SIZE));

  useEffect(() => {
    if (failureStatus && !failedPending && !failedError && failurePage > failurePages) {
      setFailurePage(failurePages);
    }
  }, [failureStatus, failedPending, failedError, failurePage, failurePages]);

  useEffect(() => {
    if (failureStatus && !failingStatuses.includes(failureStatus)) {
      setFailureStatus(failingStatuses[0] ?? null);
      setFailurePage(1);
    }
  }, [failureStatus, failingStatuses]);

  // Sucesso local e término observado após F5 renovam as leituras uma vez por run.
  const invalidatedFor = useRef(new Set<string>());
  const syncRequestedFor = useRef(new Set<string>());
  const invalidateStageReadings = useCallback((key: string, completed: boolean) => {
    if (!projectId) return;
    if (!invalidatedFor.current.has(key)) {
      invalidatedFor.current.add(key);
      void queryClient.invalidateQueries({ queryKey: stageKeys.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: ["config-server"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline", projectId] });
    }
    if (completed && !syncRequestedFor.current.has(key)) {
      syncRequestedFor.current.add(key);
      void syncOrgStructureRef.current()
        .then((result) => {
          if (result.preserved > 0) {
            toast.warning(t("{{count}} units in execution or already in the CBC kept their decision.", { count: result.preserved }));
          }
        })
        .catch((error) => {
          const reason = errorDetailOr(error, "");
          toast.error(reason
            ? t("The extraction finished, but the structure was not synced with the Diagnostic: {{reason}} Use Sync in Management.", { reason })
            : t("The extraction finished, but the structure was not synced with the Diagnostic. Use Sync in Management."));
        });
    }
  }, [projectId, queryClient, t]);
  useEffect(() => {
    if (progressStatus === "completed" && projectId) {
      invalidateStageReadings(`${projectId}:${execution.executionId ?? "completed"}`, true);
    }
  }, [progressStatus, execution.executionId, projectId, invalidateStageReadings]);
  const observedExtraction = useRef<{ projectId: string; executionId: string } | null>(null);
  useEffect(() => {
    if (!overview || !projectId) return;
    if (overview.active_run?.kind === "extraction") {
      observedExtraction.current = { projectId, executionId: overview.active_run.id };
    } else {
      const observed = observedExtraction.current;
      if (observed?.projectId === projectId) {
        const key = `${projectId}:${observed.executionId}`;
        invalidateStageReadings(key, false);
        // O estado agregado pode continuar "done" por causa de um artifact antigo.
        // Aguarda o pipeline identificar o término da run que foi observada.
        if (latestExecution?.execution_id === observed.executionId) {
          if (latestExecution.status === "completed") invalidateStageReadings(key, true);
          if (["completed", "failed", "cancelled", "canceled"].includes(latestExecution.status)) {
            observedExtraction.current = null;
          }
        }
      }
    }
  }, [overview, projectId, latestExecution?.execution_id, latestExecution?.status, invalidateStageReadings]);

  // O ready anterior volta após a falha; conserva o último domínio desta run, não dele.
  const lastActiveSnapshot = useRef<{
    projectId: string | undefined;
    executionId: string;
    overview: ConfigServerOverview;
  } | null>(null);
  useEffect(() => {
    if (overview?.active_run?.kind === "extraction") {
      lastActiveSnapshot.current = { projectId, executionId: overview.active_run.id, overview };
    }
  }, [overview, projectId]);
  const previous = lastActiveSnapshot.current;
  const progressOverview = progressStatus === "failed"
    && execution.executionId != null
    && previous?.executionId === execution.executionId
    && previous.projectId === projectId
    && overview?.active_run?.kind !== "extraction"
      ? previous.overview
      : overview;
  const states = domainNodeStates(progressOverview, progressStatus);
  const domainNodes = DOMAIN_NODES.map((node) => ({
    id: node.id, label: t(node.label), status: states[node.id],
  }));
  const current = DOMAIN_NODES.find((node) => states[node.id] === "running");
  const hasResults = (summary?.tables_total ?? 0) > 0
    || summary?.artifact_id != null
    || overview?.stages.extraction.state === "done"
    || progressStatus === "completed";
  const centralHref = `/projects/${projectId}/agents/configuration-server`;
  const openFailures = () => {
    if (!summary?.artifact_id || !currentSummary) return;
    setFailureArtifactId(summary.artifact_id);
    setFailurePage(1);
    setFailureStatus(failingStatuses[0] ?? null);
  };

  // Uma mensagem para qualquer leitura que falhou, e um botão que refaz todas.
  const failedReads: Array<() => unknown> = [];
  if (overviewError) failedReads.push(refetchOverview);
  if (scopeError) failedReads.push(refetchScope);
  if (needsLocalProvenance && localDetail.isError) failedReads.push(localDetail.refetch);
  if (pipelineError) failedReads.push(refetchPipeline);
  if (summaryError) failedReads.push(refetchSummary);
  const retryFailedReads = () => failedReads.forEach((refetch) => void refetch());
  const lastRunFailed = progressStatus === "failed"
    || (progressStatus === "idle" && !running && latestExecution?.status === "failed");
  const localReason = progressStatus === "failed"
    ? configurationErrorMessage(execution.errorCode, t) ?? execution.error
    : null;
  // O 409 de configuração já executada não é extração interrompida: a
  // mensagem dele é a principal.
  const blockedByExecution = progressStatus === "failed" && execution.errorCode === "configuration_items_already_executed";
  // Um botão por tela: com leitura falhando, só o de recarregar aparece.
  const retryLastRun = canRun && failedReads.length === 0 ? () => setShowInputSheet(true) : undefined;
  const lastRunMessage = blockedByExecution && localReason ? localReason
    : retryLastRun ? t("The last extraction did not finish. Click Retry to start a new one.")
    : t("The last extraction did not finish.");

  return (
    <div className="relative -m-6 flex min-h-[calc(100%+3rem)] flex-col gap-4">
      <StageHeader
        number="01" title={t("Extraction")} centralHref={centralHref} live={running}
        actions={
          // Extração completa travada (primeira já concluída ou configuração executada): com upload novo
          // ou Diagnóstico mudado, o botão vira o da sincronização e/ou o da atualização, que trazem só
          // o que mudou. O bloco do Diagnóstico (a frase e o botão) fica à esquerda; a atualização, à direita.
          fullExtractionLocked && hasPendingUpdate ? (
            <div className="flex flex-wrap items-center gap-2">
              {diagnosticChanged && <OrgStructureSyncAction projectId={projectId!} />}
              {updateAvailable && (
                <div className="flex flex-col items-end gap-2">
                  <Button
                    type="button" variant="action" className="h-10 gap-2 px-4 text-sm font-semibold"
                    disabled={refreshing || startRefresh.isPending}
                    onClick={() => startRefresh.mutate(undefined, {
                      onError: (error) => toast.error(errMessage(error, t("The update could not be extracted."))),
                    })}
                  >
                    {refreshing ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <RefreshCw aria-hidden="true" className="h-4 w-4" />}
                    {refreshing ? t("Extracting latest update…") : t("Extract latest update")}
                  </Button>
                  {refreshing && (
                    <ExtractionProgressBar progress={refreshProgress} label={t("Extracting latest update…")} tone="dark" className="w-[320px]" />
                  )}
                </div>
              )}
            </div>
          ) : (
            <Button type="button" variant="action" className="h-10 gap-2 px-4 text-sm font-semibold" disabled={!canRun} onClick={() => canRun && setShowInputSheet(true)}>
              <Zap aria-hidden="true" className="h-4 w-4" /> {t("Run extraction")}
            </Button>
          )
        }
      >
        {executedSomething && <p className="mt-1.5 max-w-[720px] text-sm leading-relaxed text-white/70">
          {t("A new extraction is not available after the first configuration was executed.")}
        </p>}
        {extractionDone && !executedSomething && !hasPendingUpdate && <p className="mt-1.5 max-w-[720px] text-sm leading-relaxed text-white/70">
          {t("The full extraction runs only once. When the upload or the Diagnostic changes, the update buttons appear here.")}
        </p>}
        {overview?.active_run && overview.active_run.kind !== "extraction" && <p className="mt-1.5 max-w-[720px] text-sm leading-relaxed text-white/70">
          {t("A configuration execution is in progress. Wait for it to finish before starting another extraction.")}
        </p>}
      </StageHeader>

      {failedReads.length > 0 && <div className="mx-auto w-full max-w-[1280px] px-6">
        <ConfigServerAlert message={t("Could not load this page's data.")} onRetry={retryFailedReads} />
      </div>}
      {lastRunFailed && <div className="mx-auto w-full max-w-[1280px] px-6">
        <ConfigServerAlert
          message={lastRunMessage}
          detail={blockedByExecution ? null : localReason}
          onRetry={blockedByExecution ? undefined : retryLastRun}
        />
      </div>}
      {!scopeError && scopeApproval && !scopeApproved && projectId && <div className="px-6">
        <ScopePrerequisiteBanner projectId={projectId} />
      </div>}

      <div className="mx-auto w-full max-w-[1280px] px-6">
        <ExtractionDomains nodes={domainNodes} />
      </div>
      {running && activeProgress ? (
        <div className="mx-auto w-full max-w-[1280px] px-6">
          <ExtractionProgressBar
            progress={activeProgress}
            label={current ? t("Fetching {{domain}}…", { domain: t(current.label) }) : t("Loading…")}
          />
        </div>
      ) : current && <p role="status" className="mx-auto w-full max-w-[1280px] px-6 text-sm text-[var(--exed-ink-500)]">
        {t("Fetching {{domain}}…", { domain: t(current.label) })}
      </p>}

      <div className="mx-auto w-full max-w-[1280px] px-6 pb-6">
        {summaryError ? null : summaryPending ? <div data-testid="extraction-results-pending" aria-busy="true" aria-label={t("Loading…")} className="space-y-4">
          <Skeleton aria-hidden="true" className="h-36 w-full" /><Skeleton aria-hidden="true" className="h-24 w-full" /><Skeleton aria-hidden="true" className="h-48 w-full" />
        </div> : summary && hasResults ? (
          <ExtractionDashboard
            summary={summary} onShowFailures={summary.artifact_id ? openFailures : undefined}
            readyDomains={running ? DOMAIN_NODES.filter((node) => states[node.id] === "complete").map((node) => node.id) : undefined}
          />
        ) : running ? <div className="space-y-4" aria-hidden="true">
          <Skeleton className="h-36 w-full" /><Skeleton className="h-24 w-full" /><Skeleton className="h-48 w-full" />
        </div> : <p className="text-sm text-[var(--exed-ink-500)]">{t("No extraction yet. Run the extraction to see the results.")}</p>}
      </div>

      {!summaryError && !summaryPending && summary && hasResults && <DecisionBar
        pending={summary.pending_decisions}
        centralHref={centralHref}
        managementAvailable={!running && scopeApproved && !!overview && overview.stages.management.state !== "locked"}
      />}

      <Dialog open={failureStatus !== null} onOpenChange={(open) => !open && setFailureStatus(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("Tables that could not be read")}</DialogTitle>
            <DialogDescription>{t("Tables are grouped by extraction status.")}</DialogDescription>
          </DialogHeader>
          <Tabs value={failureStatus ?? ""} onValueChange={(status) => {
            if (FAILURE_STATUSES.some((known) => known === status)) {
              setFailureStatus(status as FailureStatus); setFailurePage(1);
            }
          }}>
            <TabsList aria-label={t("Tables that could not be read")} className="h-auto w-full flex-wrap justify-start gap-0 rounded-none border-b border-[var(--exed-card-border)] bg-transparent p-0">
              {failingStatuses.map((status) => <TabsTrigger key={status} value={status} className={`group ${FAILURE_TAB_CLASS}`}>
                <span>{t(TABLE_STATUS_LABEL[status])}</span>
                <span className={FAILURE_TAB_COUNT_CLASS}>{summary?.tables_by_status[status]}</span>
              </TabsTrigger>)}
            </TabsList>
            <TabsContent value={failureStatus ?? ""} aria-busy={failedPending || undefined}>
              {failedPending ? <Skeleton aria-hidden="true" className="h-24 w-full" /> : failedError ? <div className="space-y-2">
                <p role="alert">{t("Could not load the tables. Try again.")}</p>
                <Button variant="outline" onClick={() => void refetchFailures()}>{t("Retry")}</Button>
              </div> : <div className="mt-4 max-h-[50vh] overflow-auto rounded-lg border border-[var(--exed-card-border)] bg-[var(--exed-card-bg)]">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-[#fbfcfd]">
                    <tr className="border-b border-[var(--exed-card-border)]">
                      {["Table", "Domain", "Status"].map((column) => <th key={column} scope="col" className="px-4 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--exed-ink-500)]">{t(column)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(failedTables?.items ?? []).length === 0 && <tr><td colSpan={3} className="px-4 py-3 text-sm text-[var(--exed-ink-500)]">{t("No data")}</td></tr>}
                    {(failedTables?.items ?? []).map((table) => <tr key={`${table.domain}-${table.table_name}`} className="border-t border-[var(--exed-ink-50)] first:border-t-0">
                      <th scope="row" className="px-4 py-2.5 text-left font-mono text-xs font-bold text-[var(--exed-ink-900)]">{table.table_name}</th>
                      <td className="px-4 py-2.5 text-[var(--exed-ink-700)]">{table.domain}</td>
                      <td className="px-4 py-2.5 text-[var(--exed-ink-700)]">{t(TABLE_STATUS_LABEL[table.status] ?? table.status)}</td>
                    </tr>)}
                  </tbody>
                </table>
              </div>}
              {!failedPending && !failedError && failurePages > 1 && <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <Button variant="outline" size="sm" disabled={failurePage <= 1} onClick={() => setFailurePage((page) => page - 1)}>{t("Prev")}</Button>
                <span className="text-sm">{t("Page {{page}} of {{totalPages}}", { page: failurePage, totalPages: failurePages })}</span>
                <Button variant="outline" size="sm" disabled={failurePage >= failurePages} onClick={() => setFailurePage((page) => page + 1)}>{t("Next")}</Button>
              </div>}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Sheet open={ownsUi && showInputSheet} onOpenChange={setShowInputSheet}>
        <SheetContent side="right" className="w-full sm:max-w-[480px]">
          <SheetHeader>
            <SheetTitle>{t("Run Configuration Server")}</SheetTitle>
            <SheetDescription>{t("Review the inputs before starting extraction.")}</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <AgentInputForm
              schema={schema?.inputs || []} schemaLoaded={!!schema} preparedInputs={preparedInputs?.inputs || {}} preparedSources={preparedInputs?.sources || []}
              agentId={AGENT_ID} isRunning={localExecutionStatus === "running" || localExecutionStatus === "paused"} isComplete={progressStatus === "completed"} isLocked={!canRun}
              onRun={(formData) => {
                if (!canRun || !projectId) return;
                setShowInputSheet(false);
                setExecutionProjectId(projectId);
                execution.execute(projectId, AGENT_ID, { message: "Run Configuration Server", context: formData });
                void queryClient.invalidateQueries({ queryKey: stageKeys.all(projectId) });
              }}
              onCancel={() => ownsExecution && execution.cancel()} projectId={projectId}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
